//@ts-check
import { DateTime } from 'luxon'
import { LocalStorageService } from '../services'
export default class FormatTextUtil {

    #locale = ''
    constructor() {
        this.#locale = LocalStorageService.retrieveItem('preferredLanguage')
    }
    /**
     * @author Pedro Antonio
     * @description Normalize text (exclude diacritical, included ñ and Ñ)
     * ```js
     * // Diacritical in Í from 'Martínez'
     * const name = 'PEDRO ANTONIO MARTÍNEZ'
     * return 'PEDRO ANTONIO MARTINEZ'
     * // Diacritical in Ñ from 'PIÑÓN'
     * const name = 'LUIS ALEJANDRO PIÑÓN'
     * return 'LUIS ALEJANDRO PINON'
     * ```
     * @param {string} text
     */
    static normalizeTextExcludedÑ(text) {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    }

    /**
     * @author Pedro Antonio
     * @description Normalize text (exclude diacritical, not apply to ñ and Ñ)
     * ```js
     * // Diacritical in Í from 'Martínez'
     * const name = 'PEDRO ANTONIO MARTÍNEZ'
     * return 'PEDRO ANTONIO MARTINEZ'
     * // Diacritical in Ñ from 'PIÑÓN'
     * const name = 'LUIS ALEJANDRO PIÑÓN'
     * return 'LUIS ALEJANDRO PIÑON'
     * ```
     * @param {string} text
     * @returns {string}
     */
    static normalizeTextIncludedÑ(text) {
        return text.normalize('NFD').replace(/([aeio])\u0301|(u)[\u0301\u0308]/gi, '$1$2')
    }


    /**
     * @author Pedro Antonio
     * @description Get date format and return the appropriate DateTime method
     * @param {string|Date} date - Date string or Date object
     * @returns {string} - DateTime method name (fromISO, fromSQL, fromJSDate, etc.)
     */
    getDateFormat(date) {
        // Si es un objeto Date de JavaScript
        if (date instanceof Date) {
            return 'fromJSDate'
        }

        // Si no es string, convertir a string
        if (typeof date !== 'string') {
            date = String(date)
        }

        // Remover espacios en blanco
        date = date.trim()

        // Formato ISO 8601 (YYYY-MM-DDTHH:mm:ss.sssZ o variaciones)
        const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:\d{2}|Z)?)?$/
        if (isoRegex.test(date)) {
            return 'fromISO'
        }

        // Formato SQL (YYYY-MM-DD HH:mm:ss)
        const sqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})?$/
        if (sqlRegex.test(date)) {
            return 'fromSQL'
        }

        // Formato RFC2822 (Tue, 01 Jun 2016 13:05:11 +0000)
        const rfc2822Regex = /^[A-Za-z]{3},?\s+\d{1,2}\s+[A-Za-z]{3}\s+\d{4}\s+\d{2}:\d{2}:\d{2}\s*([+-]\d{4}|\w+)?$/
        if (rfc2822Regex.test(date)) {
            return 'fromRFC2822'
        }

        // Formato HTTP (Thu, 01 Dec 1994 16:00:00 GMT)
        const httpRegex = /^[A-Za-z]{3},\s+\d{2}\s+[A-Za-z]{3}\s+\d{4}\s+\d{2}:\d{2}:\d{2}\s+GMT$/
        if (httpRegex.test(date)) {
            return 'fromHTTP'
        }

        // Milisegundos Unix timestamp (número grande)
        const millisecondsRegex = /^\d{13}$/
        if (millisecondsRegex.test(date)) {
            return 'fromMillis'
        }

        // Segundos Unix timestamp (número más pequeño)
        const secondsRegex = /^\d{10}$/
        if (secondsRegex.test(date)) {
            return 'fromSeconds'
        }

        // Formatos comunes con separadores
        const commonFormats = [
            { regex: /^\d{2}\/\d{2}\/\d{4}$/, method: 'fromFormat' }, // MM/dd/yyyy o dd/MM/yyyy
            { regex: /^\d{2}-\d{2}-\d{4}$/, method: 'fromFormat' }, // MM-dd-yyyy o dd-MM-yyyy
            { regex: /^\d{4}\/\d{2}\/\d{2}$/, method: 'fromFormat' }, // yyyy/MM/dd
            { regex: /^\d{1,2}\/\d{1,2}\/\d{4}$/, method: 'fromFormat' }, // M/d/yyyy
            { regex: /^\d{1,2}-\d{1,2}-\d{4}$/, method: 'fromFormat' }, // M-d-yyyy
        ]

        for (const format of commonFormats) {
            if (format.regex.test(date)) {
                return format.method
            }
        }

        // Si no coincide con ningún formato conocido, intentar parsearlo como formato genérico
        return 'fromFormat'
    }

    /**
     * @author Pedro Antonio
     * @description Format variable value according to the formatVariableKey
     * @param {string} variableValue
     * @param {string} formatVariableKey
     * @returns {string}
     */
    formatVariableValue(variableValue, formatVariableKey) {
        if (!variableValue || !formatVariableKey) return ''
        let value = ''
        switch (formatVariableKey) {
            case 'commaTwoDecimals':
                // Limpiar el valor removiendo comas antes de validar si es número
                value = this.formatNumber(variableValue, true, 2, true)
                break
            case 'noCommaTwoDecimals':
                value = this.formatNumber(variableValue, false, 2, true)
                break
            case 'commaOneDecimal':
                value = this.formatNumber(variableValue, true, 1, true)
                break
            case 'noCommaOneDecimal':
                value = this.formatNumber(variableValue, false, 1, true)
                break
            case 'comma':
                value = this.formatNumber(variableValue, true, 0)
                break
            case 'noComma':
                value = variableValue
                break
            case 'simpleSeparateLines':
                value = this.convertDateSimpleSeparateLines(variableValue)
                break
            case 'simpleSeparateSlide':
                value = this.convertDateSimpleSeparateSlide(variableValue)
                break
            case 'shortenedSeparateLines':
                value = this.convertDateShortenedSeparateLines(variableValue)
                break
            case 'shortenedSeparateSlides':
                value = this.convertDateShortenedSeparateSlides(variableValue)
                break
            case 'complete':
                value = this.convertDateComplete(variableValue)
                break
            case 'descriptive':
                value = this.convertDateDescriptive(variableValue)
                break
            case 'simpleText':
                value = variableValue
                break
            case 'uppercase':
                value = variableValue.toString().toUpperCase()
                break
            case 'yearAndMonth':
                value = this.convertDateInYearAndMonth(variableValue)
                break
            case 'yearAndLargeMonth':
                value = this.convertDateInYearAndLargeMonth(variableValue)
                break
            case 'onlyYear':
                value = this.convertDateOnlyYear(variableValue)
                break
            case 'onlyQuestion':
                value = variableValue
                break
            default:
                value = variableValue
                break
        }
        return value
    }

    /**
     * @author Manuel Garcia
     * @description Function to convert a plain number to a number with or without commas and with a number of decimals
     * @updatedDate 22-06-2023
     * @param {String} plainNumber
     * @param {Boolean} useCommas
     * @param {number} numberDecimals
     * @returns {String}
     */
    formatNumber(plainNumber, useCommas, numberDecimals, allowTrailingDot = false) {
        // Asegurar rango 0-2 para decimales
        const maxDec = Math.max(0, Math.min(2, Number(numberDecimals ?? 0)))

        // Convertir a string y normalizar
        let s = plainNumber != null ? String(plainNumber) : ''
        s = s.trim()
        if (!s) return ''

        // Manejar signo
        let sign = ''
        if (s[0] === '-' || s[0] === '+') {
            if (s[0] === '-') sign = '-'
            s = s.slice(1)
        }

        // Quitar comas (posibles separadores de miles de entrada)
        s = s.replace(/,/g, '')

        // Detectar si el input termina en punto (para permitir "1.")
        const originalEndsWithDot = maxDec > 0 && /\.$/.test(s)

        // Conservar solo dígitos y el primer punto decimal (si se permiten decimales)
        let cleaned = ''
        let dotSeen = false
        for (const ch of s) {
            if (ch >= '0' && ch <= '9') {
                cleaned += ch
            } else if (ch === '.' && !dotSeen && maxDec > 0) {
                cleaned += '.'
                dotSeen = true
            }
        }

        if (!cleaned) return ''

        // Partes entera y decimal
        let [intPart, decPart = ''] = cleaned.split('.')

        // Quitar ceros a la izquierda (mantener al menos un 0)
        intPart = intPart.replace(/^0+(?=\d)/, '')
        if (intPart === '') intPart = '0'

        // Limitar decimales al máximo permitido (sin redondeo)
        if (maxDec === 0) {
            decPart = ''
        } else {
            decPart = decPart.slice(0, maxDec)
        }

        // Agregar separador de miles por coma si aplica
        if (useCommas) {
            intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        }

        // Reconstruir resultado
        let result = sign + intPart
        if (maxDec > 0) {
            if (decPart.length > 0) {
                result += '.' + decPart
            } else if (allowTrailingDot && (originalEndsWithDot || dotSeen)) {
                // Mantener el punto final temporalmente
                result += '.'
            }
        }
        return result
    }


    /**
     * @author Manuel Garcia
     * @description Function to convert date with timezone in format dd/mm/yyyy
     * @updatedDate 14-12-2022
     * @param {String} date
     * @returns {String}
    */
    convertDateSimpleSeparateLines(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat('dd-MM-yyyy')
    }

    /**
    * @author Manuel Garcia
    * @description Function to convert date with timezone in format dd/mmm/yyyy
    * @updatedDate 14-12-2022
    * @param {String} date
    * @returns {String}
    */
    convertDateSimpleSeparateSlide(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat('dd/MM/yyyy')
    }

    /**
     * @author Manuel Garcia
     * @description Function to convert date with timezone in format dd/mm/yyyy
     * @updatedDate 14-12-2022
     * @param {String} date
     * @returns {String}
    */
    convertDateShortenedSeparateLines(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat('dd-MMM-yyyy')
    }

    /**
    * @author Manuel Garcia
    * @description Function to convert date with timezone in format dd/mm/yyyy
    * @updatedDate 14-12-2022
    * @param {String} date
    * @returns {String}
    */
    convertDateShortenedSeparateSlides(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat('dd/MMM/yyyy')
    }

    /**
    * @author Edgar Escobedo
    * @description Function to convert date with timezone in format year and large month
    * @updatedDate 24-04-2023
    * @param {String} date
    * @returns {String}
    */
    convertDateInYearAndLargeMonth(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat('yyyy-MMM')
    }

    /**
    * @author Edgar Escobedo
    * @description Function to convert date with timezone in format DD-MMM-YYYY
    * @updatedDate 17-08-2022
    * @param {string} date
    * @returns {String}
    */
    convertDateinDayMonthYear(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM')
    }

    /**
    * @author Edgar Escobedo
    * @description Function to convert date with timezone in format year and large month
    * @updatedDate 24-04-2023
    * @param {String} date
    * @returns {String}
    */
    convertDateOnlyYear(date) {
        return DateTime.fromISO(date).toFormat('yyyy')
    }

    /**
    * @author Manuel Garcia
    * @description Function to convert date with timezone in format dd de mes de yyyy
    * @updatedDate 14-12-2022
    * @param {String} date
    * @returns {String}
    */
    convertDateComplete(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat(`dd 'de' MMMM 'de' yyyy`)
    }

    /**
    * @author Manuel Garcia
    * @description Function to convert date with timezone in format dd de mes de yyyy
    * @updatedDate 14-12-2022
    * @param {String} date
    * @returns {String}
    */
    convertDateDescriptive(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat("'a los' dd 'días del mes de' MMMM 'del año' yyyy")
    }

    /**
    * @author Edgar Escobedo
    * @description Function to convert date with timezone in format aaaa-mm
    * @updatedDate 24-04-2023
    * @param {String} date
    * @returns {String}
    */
    convertDateInYearAndMonth(date) {
        return DateTime.fromISO(date).setLocale(this.#locale).toFormat('yyyy-MM')
    }
}
